import { Eye, EyeOff, Loader2 } from "lucide-react";
import { useMemo, useState } from "react";
import { useForm } from "react-hook-form";

import { motion } from "framer-motion";
import { Link, useNavigate } from "react-router-dom";
import { RHFSelectInput } from "./RHFSelectInput";
import { cn } from "@/lib/utils";
import { States } from "@/constants";
import { PostRequest } from "@/lib/http";
import { toast } from "sonner";
import { useDispatch } from "react-redux";
import { loginUser } from "@/redux/userReducer";
import { Button } from "../ui/button";
import { FcGoogle } from "react-icons/fc";

const SignUpForm = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [nextPage, setNextPage] = useState(false);

  const navigate = useNavigate();
  // const isMobile = useMediaQuery({ query: "(max-width: 768px)" });
  const dispatch = useDispatch();

  const initCode = useMemo(() => {
    const url = new URL(window.location.href);
    return url.searchParams.get("referral") || null;
  }, []);

  const {
    register,
    handleSubmit,
    control,
    watch,
    trigger,
    formState: { errors },
  } = useForm({
    defaultValues: {
      username: "",
      email: "",
      phone: "",
      state: "",
      password: "",
      name: "",
      pin: "",
      referral_id: initCode,
    },
    mode: "onChange",
  });

  const handleContinue = async (e) => {
    e.preventDefault();
    const isValid = await trigger(["username", "email", "phone"]);
    if (isValid) {
      setNextPage(true);
    }
  };

  const onSubmit = async (dataBody) => {
    const { data, err } = await PostRequest({
      url: "/api/register",
      body: dataBody,
      setState: setIsLoading,
      errorMessage: "An error occurred try again",
    });

    if (!err) {
      toast.success("User created successfully", {
        description: "You can now login to your account",
      });

      localStorage.setItem("user-token", data.token);
      dispatch(loginUser(data.user));
      navigate("/dashboard");
    } else {
      toast.error("Sign up error", {
        description: err,
      });
    }
  };

  return (
    <>
      <motion.form
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 0.4, ease: "easeOut" }}
        onSubmit={handleSubmit(onSubmit)}
      >
        {!nextPage ? (
          <div className="mt-5 flex flex-col gap-3">
            <div className="flex flex-col gap-1">
              <label htmlFor="username" className="text-base font-medium">
                Username
              </label>
              <input
                id="username"
                type="text"
                {...register("username", {
                  required: "Username is required",
                })}
                placeholder="Johnny"
                className="inputClass"
              />
              {errors.username && (
                <span className="text-sm font-medium text-red-500">
                  {errors.username.message}
                </span>
              )}
            </div>

            <div className="flex flex-col gap-1">
              <label htmlFor="email" className="text-base font-medium">
                Email Address
              </label>
              <input
                id="email"
                type="text"
                {...register("email", {
                  required: "Email is required",
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: "Invalid email address",
                  },
                })}
                placeholder="email@example.com"
                className="inputClass"
              />
              {errors.email && (
                <span className="text-sm font-medium text-red-500">
                  {errors.email.message}
                </span>
              )}
            </div>

            <div className="flex flex-col gap-1">
              <label htmlFor="phone" className="text-base font-medium">
                Phone Number
              </label>
              <input
                id="phone"
                type="tel"
                {...register("phone", {
                  required: "Phone number is required",
                  pattern: {
                    value:
                      /^[+]?[(]?[0-9]{3}[)]?[-\s.]?[0-9]{3}[-\s.]?[0-9]{4,6}$/,
                    message: "Invalid phone number format",
                  },
                })}
                placeholder="(070) 0022-2009"
                className="inputClass"
              />
              {errors.phone && (
                <span className="text-sm font-medium text-red-500">
                  {errors.phone.message}
                </span>
              )}
            </div>

            <p className="text-sm text-gray-500">
              By signing up or logging in, you consent to Billzpay&apos;s{" "}
              <a
                href="https://www.billzpay.com.ng/TOS"
                target="_blank"
                className="font-medium text-black dark:text-white"
              >
                Terms of Use
              </a>{" "}
              and{" "}
              <a
                href="https://www.billzpay.com.ng/privacy-policy"
                target="_blank"
                className="font-medium text-black dark:text-white"
              >
                Privacy Policy
              </a>{" "}
            </p>

            <div className="flex gap-3">
              <button
                onClick={handleContinue}
                type="button"
                className={cn(
                  `btn w-full cursor-pointer rounded-md py-2.5 font-medium text-white transition-all duration-150 disabled:pointer-events-none disabled:cursor-not-allowed lg:py-2.5`,
                )}
              >
                Continue
              </button>
            </div>
          </div>
        ) : (
          <motion.div
            initial={{ x: "100%", opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: "100%", opacity: 0 }}
            transition={{ duration: 0.5 }}
            className="mt-5 grid w-full grid-cols-2 gap-3"
          >
            <div className="col-span-2 flex flex-col gap-1">
              <label htmlFor="name" className="text-base font-medium">
                Full Name
              </label>
              <input
                id="name"
                type="text"
                {...register("name", {
                  required: "Full Name is required",
                  pattern: {
                    value: /^[a-zA-Z]+ [a-zA-Z]+$/,
                    message: "Please enter both first and last name",
                  },
                })}
                placeholder="John Doe"
                className="inputClass"
              />
              {errors.name && (
                <span className="text-sm font-medium text-red-500">
                  {errors.name.message}
                </span>
              )}
            </div>

            <div className="col-span-2 flex flex-col gap-1">
              <label htmlFor="state" className="text-base font-medium">
                State
              </label>

              <RHFSelectInput
                control={control}
                dataToMap={States}
                name="state"
                placeholder="Select a state"
                isValidate
              />

              {errors.state && (
                <span className="text-[15px] font-medium text-red-500">
                  {errors.state.message}
                </span>
              )}
            </div>

            <div className="col-span-2 flex flex-col gap-1.5 md:col-span-1">
              <label className="text-sm font-medium">Password</label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  {...register("password", {
                    required: "Password is required",
                    minLength: {
                      value: 8,
                      message: "Password must be at least 8 characters",
                    },
                  })}
                  placeholder="Enter password"
                  className="inputClass"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute top-1/2 right-3 -translate-y-1/2"
                >
                  {showPassword ? (
                    <EyeOff size={20} className="text-gray-500" />
                  ) : (
                    <Eye size={20} className="text-gray-500" />
                  )}
                </button>
              </div>

              {errors.password && (
                <p className="text-sm font-medium text-red-500">
                  {errors.password.message}
                </p>
              )}
            </div>

            <div className="col-span-2 flex flex-col gap-1.5 md:col-span-1">
              <label className="text-sm font-medium">Confirm Password</label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  {...register("confirmPassword", {
                    required: "Please confirm your password",
                    validate: (value) =>
                      value === watch("password") || "Passwords do not match",
                  })}
                  className="inputClass"
                />
              </div>

              {errors.confirmPassword && (
                <p className="text-sm font-medium text-red-500">
                  {errors.confirmPassword.message}
                </p>
              )}
            </div>

            <div className="col-span-2 flex flex-col gap-1 md:col-span-1">
              <label htmlFor="pin" className="text-base font-medium">
                Account Pin
              </label>
              <input
                id="pin"
                type="text"
                maxLength={4}
                {...register("pin", {
                  required: "Account pin is required",
                  pattern: {
                    value: /^[0-9]{4}$/,
                    message: "Pin must be exactly 4 digits",
                  },
                })}
                placeholder="1234"
                className="inputClass"
              />
              {errors.pin && (
                <span className="text-sm font-medium text-red-500">
                  {errors.pin.message}
                </span>
              )}
            </div>

            <div className="col-span-2 flex flex-col gap-1 md:col-span-1">
              <label htmlFor="referral" className="text-base font-medium">
                Referral Code
              </label>
              <input
                id="referral"
                type="text"
                {...register("referral_id")}
                placeholder="optional"
                className="inputClass"
              />
            </div>

            <div className="col-span-2 flex">
              <button
                type="submit"
                disabled={isLoading}
                className={`btn w-full rounded-sm px-8 py-3 font-medium text-white transition-all duration-150 disabled:pointer-events-none disabled:cursor-not-allowed lg:py-2`}
              >
                {isLoading ? (
                  <div className="flex items-center justify-center gap-2">
                    <Loader2 color="#fff" size={20} className="animate-spin" />
                    Signing up...
                  </div>
                ) : (
                  "Sign Up"
                )}
              </button>
            </div>

            <Button
              className="col-span-2 w-full cursor-pointer py-3 text-center text-black"
              variant={"outline"}
              onClick={() => setNextPage(false)}
            >
              Go back
            </Button>
          </motion.div>
        )}
      </motion.form>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 0.4, ease: "easeOut" }}
        className="mt-3 mb-5 flex justify-center"
      >
        <p className="cursor-pointer text-sm">
          Already have an account?{" "}
          <Link to="/login" className="font-semibold">
            Login
          </Link>
        </p>
      </motion.div>
    </>
  );
};

export default SignUpForm;
